import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAFTheme } from "@theme/.";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
import {
  CONTACTUS_TITLE1,
  CONTACTUS_TITLE2,
  CONTACTUS_TITLE3,
  EMAIL,
  FIRSTNAME,
  LASTNAME,
  MESSAGE,
} from "@constants/unicomply-keys";
import { useNavigate, useLocation } from "react-router-dom";
import { submitContactForm, ContactSubmission } from "@services/contact";
import { toast } from "react-toastify";

const ContactSection = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #ffffff 0%, #f5fdff 100%)",
  padding: "50px 27px 27px 27px",
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    padding: "24px 16px",
  },
}));
const CloseButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  right: "47px",
  top: "40px",
  minWidth: "28px",
  width: "28px",
  height: "28px",
  padding: 0,
  marginTop: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    right: "12px",
    top: "12px",
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  height: "35px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
    lineHeight: "30px",
    height: "auto",
  },
}));
const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "relative",
  alignItems: "stretch",
  [theme.breakpoints.down("lg")]: {
    gap: 16,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "center",
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 759,
  height: 637,
  borderRadius: "32px 0 0 32px",
  overflow: "hidden",
  border: "1px solid #ffffff",
  [theme.breakpoints.down("lg")]: {
    width: 640,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: 759,
    height: "auto",
    borderRadius: 24,
  },
}));

const GradientBackground = styled(Box)({
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse at 138px 40px, #065b70 0%, #0a829d 100%)",
  opacity: 0.9,
  backgroundImage: `url(${ContentUsBgImage})`,
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
});

const BackgroundPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.2,
});

const LeftContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 10,
  padding: "68px 99px",
  [theme.breakpoints.down("md")]: {
    padding: "32px 24px",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: 16,
  border: "1px solid #1d4c50",
  height: 637,
  padding: "0 64px",
  display: "flex",
  alignItems: "center",
  position: "relative",
  marginLeft: -104,
  zIndex: 10,
  [theme.breakpoints.down("lg")]: {
    marginLeft: -32,
  },
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    height: "auto",
    padding: "24px 24px",
  },
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    width: "90%",
    height: "auto",
    padding: "24px 24px",
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: 450,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
}));

const CustomTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: 0,
    border: "none",
    borderBottom: "1px solid #065b70",
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "& input": {
      padding: "16px 12px",
      height: "18px",
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 600,
    color: "#1f2937",
    fontSize: 14,
    lineHeight: "20px",
    transform: "translate(12px, -9px) scale(1)",
  },
});

const CustomTextArea = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: 0,
    border: "none",
    borderBottom: "1px solid #065b70",
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "& textarea": {
      padding: "12px 12px",
      resize: "none",
      paddingLeft: "0px",
      maxHeight: "35px",
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 600,
    color: "#1f2937",
    fontSize: 14,
    lineHeight: "20px",
    transform: "translate(12px, -9px) scale(1)",
    alignSelf: "stretch",
  },
  //   transform: "translate(12px, 16px) scale(1)",
  //   // prevent floating animation unless focused
  //   "&.MuiInputLabel-shrink": {
  //     transform: "translate(12px, -6px) scale(0.85)",
  //   },
});

const SubmitButton = styled(Button)({
  width: "100%",
  backgroundColor: "#0A829D",
  color: "#fff",
  fontWeight: 600,
  fontSize: 16,
  lineHeight: "24px",
  padding: "16px 20px",
  borderRadius: 8,
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0a829d",
    color: "#ffffff",
  },
  transition: "all 0.2s ease",
});

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  captchaAnswer: string;
}

export default function UniComply_Contact() {
  useAFTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPenalty = location.state?.from === "penalty";
  const fromPricingContactUsFlow =
    (location.state as any)?.fromPricingContactUsFlow === true;
  const facilityId = location.state?.facilityId;

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    captchaAnswer: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaData, setCaptchaData] = useState<{
    question: string;
    answer: string;
    type: "visual";
  } | null>(null);
  const [captchaError, setCaptchaError] = useState<string>("");

  const handleClose = () => {
    if (fromPenalty) {
      navigate("/dashboard/MyFacility", {
        state: { activeTab: "penalty", facilityId },
      });
      return;
    }
    // If a generic back target was provided
    if (location.state?.backTo) {
      const { backTo, backState } = location.state as any;
      navigate(backTo, { state: backState });
      return;
    }
    // Try to go back; if no history, fallback to MyFacility
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/dashboard/MyFacility");
    }
  };

  // Generate visual CAPTCHA with scrambled characters
  const generateCaptcha = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const length = 6 + Math.floor(Math.random() * 3); // 6-8 characters
    let answer = "";

    // Generate random characters
    for (let i = 0; i < length; i++) {
      answer += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Create scrambled display (no spaces)
    const scrambled = answer
      .split("")
      .map((char) => ({ char }))
      .sort(() => Math.random() - 0.5);

    const displayText = scrambled.map((item) => item.char).join("");

    // Store the clean answer (without spaces/superscript formatting) for validation
    const cleanAnswer = scrambled.map((item) => item.char).join("");

    setCaptchaData({
      question: displayText,
      answer: cleanAnswer,
      type: "visual",
    });
    setCaptchaError("");
  };

  // Generate CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear CAPTCHA error when user starts typing
    if (captchaError) {
      setCaptchaError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.email.trim() ||
      !formData.message.trim()
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // CAPTCHA validation
    if (!formData.captchaAnswer.trim()) {
      setCaptchaError("Please solve the CAPTCHA");
      toast.error("Please solve the CAPTCHA to continue");
      return;
    }

    const userAnswer = formData.captchaAnswer.trim().toUpperCase();

    if (!captchaData) {
      setCaptchaError("CAPTCHA not loaded. Please refresh the page.");
      toast.error("CAPTCHA not loaded. Please refresh the page.");
      return;
    }

    const correctAnswer = captchaData.answer.toUpperCase();

    // Debug logging
    console.log("User answer:", userAnswer);
    console.log("Correct answer:", correctAnswer);
    console.log("CAPTCHA question:", captchaData.question);

    if (userAnswer !== correctAnswer) {
      setCaptchaError("Invalid CAPTCHA. Please try again.");
      toast.error("CAPTCHA verification failed. Please try again.");
      generateCaptcha(); // Generate new CAPTCHA
      setFormData((prev) => ({ ...prev, captchaAnswer: "" }));
      return;
    }

    setIsSubmitting(true);

    try {
      const contactData: ContactSubmission = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };

      await submitContactForm(contactData);

      toast.success("Thank you for your message! We'll get back to you soon.");

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        message: "",
        captchaAnswer: "",
      });
      generateCaptcha(); // Generate new CAPTCHA for next submission

      // Redirect based on where the user came from
      setTimeout(() => {
        if (fromPenalty) {
          navigate("/dashboard/MyFacility", {
            state: {
              activeTab: "penalty",
              facilityId: facilityId,
            },
          });
        } else if (fromPricingContactUsFlow) {
          navigate("/dashboard/pricing");
        } else {
          navigate("/dashboard/MyFacility", {
            state: { currentView: "spaces" },
          });
        }
      }, 2000); // 2 second delay to show success message
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <Box sx={{ position: "relative" }}>
        <MainContainer>
          {/* Left Panel - Branded Content */}
          <Box sx={{ position: "relative" }}>
            {/* Black background frame */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#000000",
                borderRadius: "32px",
                border: "1px solid #ffffff",
                width: { xs: "100%", md: 758.5 },
                height: { xs: "100%", md: 637 },
                display: { xs: "none", md: "block" },
              }}
            />

            <LeftPanel>
              <GradientBackground />

              {/* Background patterns - simplified for now */}
              <BackgroundPattern>
                <Box
                  sx={{
                    position: "absolute",
                    top: "5.2%",
                    left: "-188.9%",
                    width: 2274.63,
                    height: 828.745,
                    transform: "rotate(180deg)",
                    background:
                      "radial-gradient(ellipse at center, rgba(255,255,255,0.1) 0%, transparent 70%)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "-15.2%",
                    left: "-236.12%",
                    width: 2558.7,
                    height: 714.648,
                    transform: "rotate(180deg)",
                    background:
                      "radial-gradient(ellipse at center, rgba(255,255,255,0.05) 0%, transparent 70%)",
                  }}
                />
              </BackgroundPattern>

              <LeftContent>
                {/* Tagline */}
                <Typography
                  sx={{
                    color: "#E3E3E3",
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: { xs: "14px", sm: "15px" },
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "normal",
                  }}
                >
                  {CONTACTUS_TITLE1}
                </Typography>

                {/* Main heading */}
                <Typography
                  sx={{
                    color: "#FFF",
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: {
                      xs: "40px",
                      sm: "52px",
                      md: "64px",
                      lg: "74px",
                    },
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "normal",
                    marginBottom: 12,
                    mt: 12,
                  }}
                >
                  {CONTACTUS_TITLE2}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: "var(--White, #FFF)",
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontSize: { xs: "14px", sm: "15px" },
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "150%", // 30px
                    width: { xs: "100%", sm: "400px" },
                  }}
                >
                  {CONTACTUS_TITLE3}
                </Typography>
              </LeftContent>
            </LeftPanel>
          </Box>

          {/* Right Panel - Contact Form */}
          <RightPanel>
            <CloseButton onClick={handleClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="29"
                height="29"
                viewBox="0 0 29 29"
                fill="none"
              >
                <g clipPath="url(#clip0_358_243)">
                  <path
                    d="M14.5 0.25C6.76806 0.25 0.5 6.51806 0.5 14.25C0.5 21.9824 6.76806 28.25 14.5 28.25C22.2324 28.25 28.5 21.9824 28.5 14.25C28.5 6.51806 22.2324 0.25 14.5 0.25ZM14.5 26.5276C7.74544 26.5276 2.25 21.0046 2.25 14.2499C2.25 7.49538 7.74544 1.99995 14.5 1.99995C21.2546 1.99995 26.75 7.49541 26.75 14.2499C26.75 21.0045 21.2546 26.5276 14.5 26.5276ZM19.4494 9.30056C19.1077 8.95887 18.5539 8.95887 18.2122 9.30056L14.5 13.0128L10.7878 9.30056C10.4461 8.95887 9.89225 8.95887 9.55013 9.30056C9.20844 9.64225 9.20844 10.1961 9.55013 10.5378L13.2623 14.25L9.55013 17.9622C9.20844 18.3034 9.20844 18.8582 9.55013 19.1995C9.89181 19.5412 10.4457 19.5412 10.7878 19.1995L14.5 15.4873L18.2122 19.1995C18.5539 19.5412 19.1077 19.5412 19.4494 19.1995C19.7911 18.8582 19.7911 18.3034 19.4494 17.9622L15.7372 14.25L19.4494 10.5378C19.7916 10.1957 19.7916 9.64181 19.4494 9.30056Z"
                    fill="#033843"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_358_243">
                    <rect
                      width="28"
                      height="28"
                      fill="white"
                      transform="translate(0.5 0.25)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </CloseButton>

            <FormContainer>
              <PageTitle>Let's get in touch</PageTitle>

              <form onSubmit={handleSubmit}>
                {/* Name Fields Row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: { xs: 2, sm: 3, md: 4 },
                    marginBottom: 4,
                    marginTop: 3,
                  }}
                >
                  <CustomTextField
                    label={FIRSTNAME}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                  <CustomTextField
                    label={LASTNAME}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                </Box>

                {/* Email Field */}
                <Box sx={{ marginBottom: 4 }}>
                  <CustomTextField
                    label={EMAIL}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                </Box>

                {/* Message Field */}
                <Box sx={{ marginBottom: 4 }}>
                  <CustomTextArea
                    label={MESSAGE}
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    multiline
                    rows={5}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                </Box>

                {/* Visual CAPTCHA Section */}
                <Box sx={{ marginBottom: 3 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#495057",
                      fontSize: "14px",
                      marginBottom: 1,
                    }}
                  >
                    Security Verification:
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 2,
                      marginBottom: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* CAPTCHA Display Field */}
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "150px" },
                        padding: "8px 12px",
                        border: "2px solid #e74c3c",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        minHeight: { xs: "40px", sm: "36px" },
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "monospace",
                        fontSize: { xs: "14px", sm: "16px" },
                        fontWeight: 600,
                        letterSpacing: "1px",
                        color: "#2c3e50",
                      }}
                    >
                      {captchaData ? captchaData.question : "Loading..."}
                    </Box>

                    {/* Reload Button */}
                    <Button
                      onClick={generateCaptcha}
                      disabled={isSubmitting}
                      sx={{
                        minWidth: "36px",
                        height: "36px",
                        backgroundColor: "#27ae60",
                        color: "white",
                        borderRadius: "6px",
                        fontSize: "16px",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#229954",
                        },
                        "&:disabled": {
                          backgroundColor: "#bdc3c7",
                        },
                      }}
                    >
                      ↻
                    </Button>

                    {/* CAPTCHA Answer Input */}
                    <CustomTextField
                      label="CAPTCHA Verification"
                      name="captchaAnswer"
                      value={formData.captchaAnswer}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      error={!!captchaError}
                      helperText={captchaError}
                      placeholder="e.g., A4B7K9"
                      sx={{
                        maxWidth: { xs: "100%", sm: "200px" },
                        "& .MuiFormLabel-root": {
                          fontSize: "12px",
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Submit Button */}
                <SubmitButton
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </SubmitButton>
              </form>
            </FormContainer>
          </RightPanel>
        </MainContainer>
      </Box>
    </ContactSection>
  );
}
